import type { FAQItem } from "@/data/faqs";

interface FAQSectionProps {
  title?: string;
  items: FAQItem[];
  /** Optional id for the heading, used for aria-labelledby. */
  id?: string;
  className?: string;
}

/**
 * Accessible FAQ accordion built on native <details>/<summary>.
 * All answer text is present in the DOM on initial render so search engines
 * and LLM crawlers can index it without executing JavaScript. The FAQPage
 * JSON-LD is emitted separately by the page-level <SEO /> component so the
 * visible Q&A and the structured data stay in sync via src/data/faqs.ts.
 */
const FAQSection = ({
  title = "Frequently asked questions",
  items,
  id = "faq",
  className = "",
}: FAQSectionProps) => {
  if (!items?.length) return null;

  return (
    <section
      aria-labelledby={`${id}-heading`}
      className={`py-16 md:py-20 ${className}`}
    >
      <div className="container mx-auto max-w-3xl px-4">
        <h2
          id={`${id}-heading`}
          className="mb-8 text-2xl font-bold tracking-tight text-foreground md:text-3xl"
        >
          {title}
        </h2>
        <div className="divide-y divide-border rounded-2xl border border-border bg-card">
          {items.map((item, i) => (
            <details
              key={i}
              className="group px-5 py-4 md:px-6 md:py-5 [&_summary::-webkit-details-marker]:hidden"
            >
              <summary
                className="flex cursor-pointer list-none items-start justify-between gap-4 text-base font-semibold text-foreground md:text-lg"
                itemProp="name"
              >
                <span>{item.question}</span>
                <span
                  aria-hidden="true"
                  className="mt-1 inline-block shrink-0 text-primary transition-transform duration-200 group-open:rotate-45"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </span>
              </summary>
              <div className="mt-3 text-sm leading-relaxed text-muted-foreground md:text-base">
                {item.answer}
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * Build a schema.org FAQPage JSON-LD object from FAQ items.
 * Pass the result into <SEO jsonLd={...} /> on the same page that renders
 * the matching <FAQSection /> so visible text and structured data match.
 */
export const buildFaqJsonLd = (items: FAQItem[]) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
});

export default FAQSection;
